import { useCallback } from 'react';

import { UseUpdateUserInformationReturnType } from './useUpdateUserInformation.decl';

import { UpdateAgentInput } from '@generated/globalTypes';
import { UpdateAgentMutation, UpdateAgentMutationVariables } from '@generated/UpdateAgentMutation';
import { UserFullFragment } from '@generated/UserFullFragment';
import { UPDATE_AGENT_MUTATION } from '@graphql/mutations/UpdateAgentMutation';
import { ClientException, handleFormErrors } from '@helpers/errors.helpers';
import { getTrackSettingsProperties } from '@helpers/tracker.helpers';
import { useGraphMutation } from '@hooks/useMutation';
import { useTracker } from '@hooks/useTracker/useTracker';

const profileSettings: (keyof UpdateAgentInput)[] = ['role', 'language', 'extension'];
const personalInformation: (keyof UpdateAgentInput)[] = ['firstName', 'lastName'];
const availability: (keyof UpdateAgentInput)[] = ['timeZone', 'state'];
const callPreferences: (keyof UpdateAgentInput)[] = [
  'useAircallPhoneNumberAsCallerID',
  'defaultPrefix',
  'currentRingtone',
  'wrapUpTime',
];
const userFields = [
  ...profileSettings,
  ...personalInformation,
  ...callPreferences,
  ...availability,
];

/**
 * Hook to update user's information given a user and a predicate.
 * @param user - the user to update
 * @param isOwnUser - user is own user
 * @returns an object holding a function responsible for updating a user's information
 */
export function useUpdateUserInformation(
  user: UserFullFragment,
  isOwnUser: boolean
): UseUpdateUserInformationReturnType {
  const { track } = useTracker();
  const [mutate] = useGraphMutation<UpdateAgentMutation, UpdateAgentMutationVariables>(
    UPDATE_AGENT_MUTATION
  );

  const updateUser = useCallback(
    async (input: UserFullFragment) => {
      try {
        await mutate({
          variables: {
            input: {
              ID: input.ID,
              currentRingtone: input.currentRingtone,
              defaultOutPhoneNumberID: input.defaultOutPhoneNumberID,
              defaultPrefix: input.defaultPrefix,
              extension: input.extension,
              firstName: input.firstName,
              language: input.language,
              lastName: input.lastName,
              ringTimeout: input.ringTimeout,
              role: input.role,
              state: input.state,
              timeZone: input.timeZone,
              useAircallPhoneNumberAsCallerID: input.useAircallPhoneNumberAsCallerID,
              wrapUpTime: input.wrapUpTime,
            },
          },
        });

        userFields.forEach((field) => {
          const isFieldUpdated = user[field] !== input[field];
          const isGeneralSection = [...profileSettings, ...personalInformation].includes(field);
          const settingsSection = isGeneralSection ? 'General' : 'Call preferences';
          let setting = settingsSection;

          if (isGeneralSection) {
            setting = profileSettings.includes(field) ? 'Profile settings' : 'Personal information';
          }

          if (isFieldUpdated) {
            track(
              getTrackSettingsProperties({
                event: 'user_settings_edited',
                settingsSection,
                setting,
                field,
                isOwnUser,
              })
            );
          }
        });

        return null;
      } catch (err) {
        return handleFormErrors(err as ClientException);
      }
    },
    [isOwnUser, mutate, track, user]
  );

  return { updateUser };
}
